import React, { useState } from "react";
import styled from "styled-components";
import {
  Paper,
  Typography,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material";
import API from "src/axios/instances/API";
import CheckboxBrandBasic from "src/components/UI/CheckboxBrandBasic";

const Wrapper = styled(Paper)`
  padding: 20px;
  width: 440px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  border: 2px solid mediumseagreen;
  border-radius: 8px;
`;

const Item = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid lightgray;
  border-radius: 4px;
  background-color: ${(props) => (props?.$isActive ? "#eeeeee80" : "white")};
`;

const ListWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 340px;
  overflow-y: scroll;
`;

const NameText = styled.span`
  color: darkgrey;
  line-height: 1.2;
  font-size: 12px;
`;

const EmailText = styled.span`
  color: black;
  line-height: 1.2;
  font-weight: 400;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  border-radius: inherit;
`;

const getErrorMessaging = (error) => {
  if (error?.response?.data?.error?.title) {
    return {
      title: error?.response?.data?.error?.title || "Error",
      details:
        error?.response?.data?.error?.details ||
        "There was an error approving these team members.",
    };
  }

  return {
    title: "Error",
    details: "There was an error approving these team members.",
  };
};

const ApproveBulkModal = ({
  pending,
  setSnackbarText,
  pendingMutate,
  teamMutate,
  setApproveAllOpen,
}) => {
  const [isApproving, setIsApproving] = useState(false);
  const [approveArr, setApproveArr] = useState(pending.map((p) => p.id));
  const [errorAlert, setErrorAlert] = useState(null);

  const handleApproveAll = async () => {
    try {
      setIsApproving(true);
      await API.post("dashboard/teams/bulk-approve-pending-team-members/", {
        team_member_ids: approveArr,
      });
      await pendingMutate();
      teamMutate();
      setIsApproving(false);
      setApproveAllOpen(false);
      setSnackbarText("Teams members approved success");
    } catch (error) {
      const errorObj = getErrorMessaging(error);
      setErrorAlert(errorObj);
      setIsApproving(false);
    }
  };

  const handleClick = (id) => {
    if (approveArr.includes(id)) {
      const newArr = approveArr.filter((num) => num !== id);

      setApproveArr(newArr);
    } else {
      setApproveArr((p) => [...p, id]);
    }
  };

  const isDisabled = approveArr.length == 0 || isApproving;

  return (
    <Wrapper>
      <Typography variant="h5" component="h5">
        Approve Multiple Team Member Requests
      </Typography>
      <Typography
        sx={{ mb: 3 }}
        style={{ fontWeight: "400", fontSize: 13, color: "darkgrey" }}
      >
        {pending.length} Outstanding Access
        {pending.length === 1 ? " Request" : " Requests"}
      </Typography>
      <ListWrapper>
        {pending.map((p) => (
          <Item
            key={p.id}
            onClick={() => handleClick(p.id)}
            $isActive={approveArr.includes(p.id)}
          >
            <TextContainer>
              <EmailText>{p.user?.email}</EmailText>
              <NameText>
                {p.user?.firstName} {p.user?.lastName}
              </NameText>
            </TextContainer>
            <CheckboxBrandBasic
              overrideColor="mediumseagreen"
              checked={approveArr.includes(p.id)}
            />
          </Item>
        ))}
      </ListWrapper>
      <div style={{ minHeight: 12 }} />
      <Button
        fullWidth
        variant="contained"
        disabled={isDisabled}
        style={{
          borderRadius: 6,
          backgroundColor: isDisabled ? "lightgray" : "mediumseagreen",
        }}
        onClick={handleApproveAll}
        startIcon={
          isApproving && <CircularProgress size={16} sx={{ color: "black" }} />
        }
      >
        {" "}
        {isApproving ? "..." : "Approve"}
      </Button>
      {errorAlert && (
        <Alert
          style={{ textAlign: "left", border: "1px solid firebrick" }}
          sx={{ mt: 2 }}
          severity="error"
          onClose={() => setErrorAlert(null)}
        >
          <p style={{ fontWeight: 600, fontSize: 14 }}>{errorAlert?.title}</p>
          <span>{errorAlert?.details}</span>
        </Alert>
      )}
    </Wrapper>
  );
};

export default ApproveBulkModal;
